import React from "react";
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Stack,
  Select,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import {
  LocalShipping,
  HelpOutline,
  Verified,
  ArrowDropDown,
  ShoppingCart,
  CheckCircle,
  Favorite,
  FavoriteBorder,
  Share,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface ProductPurchaseCardProps {
  price: number;
  condition: string;
  quantity: number;
  setQuantity: (val: number) => void;
  justAdded: boolean;
  onAddToCart: () => void;
  isFavorite: boolean;
  setIsFavorite: (val: boolean) => void;
  getConditionColor: (condition: string) => string;
}

const ProductPurchaseCard: React.FC<ProductPurchaseCardProps> = ({
  price,
  condition,
  quantity,
  setQuantity,
  justAdded,
  onAddToCart,
  isFavorite,
  setIsFavorite,
  getConditionColor,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        bgcolor: "var(--bg-secondary)",
        p: 3,
        borderRadius: 0,
        border: "1px solid var(--border-primary)",
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
        {/* Price Section */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Chip
              label={t("products.ourBestPrice")}
              size="small"
              sx={{
                bgcolor: "#4a5568",
                color: "white",
                fontWeight: 500,
                height: 28,
              }}
            />
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "var(--primary-color)",
              mt: 1,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            €
            {new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(price)}{" "}
            {t("products.exclVAT")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <LocalShipping fontSize="small" />
            {t("products.shippingIncluded")} ({t("regions.europe")})
          </Typography>
        </Box>

        {/* Product Details */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 3,
            gridColumn: "1 / -1",
          }}
        >
          <Box>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {t("products.condition")}
                  <Tooltip title={t("products.conditionTooltip")}>
                    <HelpOutline sx={{ fontSize: 16, cursor: "pointer" }} />
                  </Tooltip>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: getConditionColor(condition || "good"),
                  }}
                >
                  {t(`products.${condition || "good"}`)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("products.year")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {"2014"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("products.stock")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {1}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {t("products.typeOfSeller")}
                  <Tooltip title={t("products.certifiedSellerTooltip")}>
                    <HelpOutline sx={{ fontSize: 16, cursor: "pointer" }} />
                  </Tooltip>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Verified sx={{ color: "#ff9800", fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t("products.certifiedProfessionalSeller")}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("products.originOfSeller")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={{ fontSize: "1.2rem" }}>
                    🇫🇷
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t("countries.france")}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Offer Details Section */}
      <Box sx={{ borderTop: "1px solid var(--border-primary)", pt: 2, mt: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: "var(--text-primary)",
            fontSize: "1rem",
          }}
        >
          {t("products.offerDetails")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography
            variant="body1"
            sx={{ color: "var(--text-primary)", fontSize: "0.875rem" }}
          >
            {t("products.quantity")}:
          </Typography>
          <Select
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            size="small"
            IconComponent={ArrowDropDown}
            sx={{
              minWidth: 60,
              height: 32,
              "& .MuiSelect-select": {
                py: 0.5,
                fontSize: "0.875rem",
              },
            }}
          >
            {[1, 2, 3, 4, 5].map((num) => (
              <MenuItem key={num} value={num}>
                {num}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Add to Cart Button */}
        <Box sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="contained"
            size="large"
            onClick={onAddToCart}
            disabled={justAdded}
            startIcon={justAdded ? <CheckCircle /> : <ShoppingCart />}
            sx={{
              bgcolor: justAdded ? "var(--success-color)" : "var(--primary-color)",
              color: "white",
              px: 2,
              flex: 1,
              py: 1,
              fontSize: "1rem",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              "&:hover": {
                bgcolor: justAdded ? "var(--success-color)" : "var(--link-hover)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
              },
              "&:disabled": {
                bgcolor: "var(--success-color)",
                color: "white",
              },
            }}
          >
            {justAdded ? t("products.addedToCart") : t("products.addToCart")}
          </Button>

          <IconButton
            onClick={() => setIsFavorite(!isFavorite)}
            sx={{
              border: "1px solid var(--border-primary)",
              "&:hover": {
                borderColor: "var(--danger-color)",
                bgcolor: "rgba(var(--danger-rgb),0.04)",
              },
            }}
          >
            {isFavorite ? (
              <Favorite sx={{ color: "var(--danger-color)", fontSize: 20 }} />
            ) : (
              <FavoriteBorder sx={{ color: "var(--text-secondary)", fontSize: 20 }} />
            )}
          </IconButton>

          <IconButton
            sx={{
              border: "1px solid var(--border-primary)",
              "&:hover": {
                borderColor: "var(--primary-color)",
                bgcolor: "rgba(var(--primary-rgb),0.04)",
              },
            }}
          >
            <Share sx={{ color: "var(--text-secondary)", fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductPurchaseCard;